import React, { useEffect, useRef } from 'react';
import { ChatBubble, ChatBubbleProps } from './ChatBubble';
import { TypingIndicator } from '../../components/Chat/TypingIndicator';
import { ChatIcon } from '../../components/Chat/ChatIcons';
import { Persona } from '../../lib/api/Persona';
import { ChatUiMessage } from '../../lib/interfaces/Chat';

export interface ChatPaneProps extends Omit<ChatBubbleProps, 'message'> {
    messages: ChatUiMessage[];
    isLoading?: boolean;
    header?: string;
    useWatermark?: boolean;
    className?: string;
    chatId?: string;
    persona: Persona | null | undefined;
    showWorkflowSteps?: boolean;
}

export const ChatPane: React.FC<ChatPaneProps> = ({
    messages,
    isLoading,
    header,
    useWatermark,
    className = '',
    persona,
    showClassification,
    showWorkflowSteps,
    userEmail,
    onFeedback,
    onSpeak,
    onStopSpeak,
    currentlyPlayingId,
    returnSpeak,
    chatId,
}) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isLoading]);

    const isEmpty = messages.length === 0;

    const content = (
        <>
            {isEmpty ? (
                <div className={`empty-state ${useWatermark ? 'empty-state-watermark' : 'split-pane-empty'}`}>
                    {useWatermark && persona?.imageURL ? (
                        <img
                            src={persona.imageURL}
                            alt=""
                            aria-hidden="true"
                            className="empty-state-watermark-image"
                        />
                    ) : (
                        <div className="empty-state-icon">
                            <ChatIcon />
                        </div>
                    )}
                </div>
            ) : (
                <>
                    {messages.map((message) => (
                        <ChatBubble
                            key={message.id}
                            message={message}
                            showClassification={showClassification}
                            showWorkflowSteps={showWorkflowSteps}
                            userEmail={userEmail}
                            persona={persona}
                            onFeedback={onFeedback}
                            onSpeak={onSpeak}
                            onStopSpeak={onStopSpeak}
                            currentlyPlayingId={currentlyPlayingId}
                            returnSpeak={returnSpeak}
                            chatId={message.streamChatId || chatId}
                        />
                    ))}
                    {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
                        <TypingIndicator persona={persona} />
                    )}
                    <div ref={messagesEndRef} />
                </>
            )}
        </>
    );

    if (header) {
        return (
            <section className={`split-chat-pane ${className}`} aria-label={header}>
                <div className="split-chat-pane-header">
                    <span>{header}</span>
                </div>
                <div className="split-chat-pane-body">
                    {content}
                </div>
            </section>
        );
    }

    return content;
};
